import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { BookOpen, Search, Plus, Eye } from "lucide-react";
import knowledgeService from "../services/knowledgeService";
import ArticleEditorModal from "../components/knowledge/ArticleEditorModal";
import { usePermissions } from "../context/PermissionsContext";
import { fmtDateOnly } from "../utils/datetime";

const stripHtml = (html) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  return (tmp.textContent || tmp.innerText || "").trim();
};

const BaseConhecimento = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const canCreate = can("create", "/base-conhecimento");

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);

  // "Criar artigo a partir do chamado": abre o editor pré-preenchido.
  useEffect(() => {
    if (location.state?.prefill && canCreate) {
      setPrefill(location.state.prefill);
      setIsEditorOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint pattern do projeto: sem exhaustive-deps
  }, []);

  const load = () => {
    setLoading(true);
    knowledgeService
      .list({ q: search || undefined, category: category || undefined })
      .then((data) => setArticles(Array.isArray(data) ? data : []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint pattern do projeto: sem exhaustive-deps
  }, [search, category]);

  const categories = useMemo(() => {
    const set = new Set();
    articles.forEach((a) => a.category && set.add(a.category));
    return Array.from(set).sort();
  }, [articles]);

  const handleCreate = async (data) => {
    try {
      const created = await knowledgeService.create(data);
      setIsEditorOpen(false);
      navigate(`/base-conhecimento/${created.id}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao criar artigo.");
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Base de Conhecimento</h1>
            <p className="text-gray-500">Artigos, soluções e procedimentos da equipe de TI.</p>
          </div>
          {canCreate && (
            <Button onClick={() => setIsEditorOpen(true)} className="gap-2">
              <Plus size={16} /> Novo artigo
            </Button>
          )}
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por título, conteúdo ou tag..."
                  className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas as categorias</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-500">Carregando...</div>
        ) : articles.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-gray-500">
            <BookOpen className="mx-auto mb-3 h-8 w-8 text-gray-300" />
            Nenhum artigo encontrado.
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map((a) => (
              <Card
                key={a.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/base-conhecimento/${a.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter") navigate(`/base-conhecimento/${a.id}`); }}
                className="cursor-pointer transition-shadow hover:shadow-md"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    {a.category && (
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">{a.category}</span>
                    )}
                    {a.status !== "publicado" && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Rascunho</span>
                    )}
                    {a.is_public && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Público</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 line-clamp-2">{a.title}</h3>
                  <p className="mt-1 text-sm text-gray-500 line-clamp-3">{stripHtml(a.body) || "—"}</p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><Eye size={13} /> {a.views || 0}</span>
                    <span>· {fmtDateOnly(a.updated_at) || ""}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <ArticleEditorModal
        isOpen={isEditorOpen}
        onClose={() => { setIsEditorOpen(false); setPrefill(null); }}
        article={prefill}
        onSubmit={handleCreate}
      />
    </Layout>
  );
};

export default BaseConhecimento;
